import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  User, BookOpen, Calendar, CheckCircle2, XCircle, Clock,
  AlertCircle, Award, MessageSquare, Loader2, GraduationCap,
  TrendingUp, FileText
} from 'lucide-react';

export default function ParentProgressPage() {
  const { api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);

  useEffect(() => {
    fetchLearners();
  }, []);

  useEffect(() => {
    if (selectedLearner) {
      fetchProgress();
      // Update URL when learner changes
      setSearchParams({ learner_id: selectedLearner });
    }
  }, [selectedLearner]);

  const fetchLearners = async () => {
    try {
      const res = await api.get('/learners/');
      const learnerData = res.data.results || res.data || [];
      setLearners(learnerData);
      
      // Check if there's a learner_id in the URL
      const urlLearnerId = searchParams.get('learner_id');
      
      if (urlLearnerId && learnerData.some(l => l.id === urlLearnerId)) {
        // Use the learner from URL if valid
        setSelectedLearner(urlLearnerId);
      } else if (learnerData.length > 0) {
        // Default to first learner
        setSelectedLearner(learnerData[0].id);
      }
    } catch (error) {
      toast.error('Failed to load learners');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    setProgressLoading(true);
    try {
      const res = await api.get(`/parent/progress?learner_id=${selectedLearner}`);
      setProgress(res.data);
    } catch (error) {
      console.error(error);
      setProgress(null);
    } finally {
      setProgressLoading(false);
    }
  };

  const getAttendanceColor = (rate) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 75) return 'text-amber-600';
    return 'text-red-600';
  };

  const getGradeColor = (grade) => {
    if (grade >= 90) return 'bg-green-100 text-green-700';
    if (grade >= 80) return 'bg-blue-100 text-blue-700';
    if (grade >= 70) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const getNoteTypeIcon = (type) => {
    switch (type) {
      case 'achievement': return <Award className="h-4 w-4 text-amber-500" />;
      case 'progress': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'concern': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <MessageSquare className="h-4 w-4 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (learners.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No children added yet</h3>
          <p className="text-muted-foreground">Add your child to start tracking their progress</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-progress-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Child Progress</h1>
          <p className="text-muted-foreground">Track your child's attendance, grades, and instructor feedback</p>
        </div>
      </div>

      {/* Child Selector */}
      <div className="flex items-center gap-4">
        <Select value={selectedLearner} onValueChange={setSelectedLearner}>
          <SelectTrigger className="w-[250px]" data-testid="learner-selector">
            <User className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Select a child" />
          </SelectTrigger>
          <SelectContent>
            {learners.map(learner => (
              <SelectItem key={learner.id} value={learner.id}>
                {learner.first_name} {learner.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {progressLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : progress ? (
        <>
          {/* Learner Summary Card */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">
                    {progress.learner?.name?.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold">{progress.learner?.name}</h2>
                  <p className="text-muted-foreground">
                    Grade {progress.learner?.grade_level} • {progress.learner?.points_balance || 0} points earned
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Course Progress */}
          {progress.courses?.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No active enrollments</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {progress.courses?.map((course, idx) => (
                <Card key={idx} data-testid={`course-progress-${idx}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <BookOpen className="h-5 w-5 text-primary" />
                          {course.course_title}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {course.location} • {course.start_date} to {course.end_date}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {course.attendance_rate !== null && (
                          <Badge className={getAttendanceColor(course.attendance_rate)}>
                            {course.attendance_rate}% Attendance
                          </Badge>
                        )}
                        {course.average_grade !== null && (
                          <Badge className={getGradeColor(course.average_grade)}>
                            {course.average_grade}% Average
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="multiple" defaultValue={['attendance', 'assignments', 'notes']}>
                      {/* Attendance */}
                      <AccordionItem value="attendance">
                        <AccordionTrigger>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Attendance
                            <Badge variant="outline" className="ml-2">
                              {course.attendance?.total || 0} classes
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {course.attendance?.total === 0 ? (
                            <p className="text-muted-foreground text-sm py-2">No attendance records yet</p>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
                              <div className="text-center p-3 bg-green-50 rounded-lg">
                                <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto mb-1" />
                                <p className="text-2xl font-bold text-green-600">{course.attendance?.present}</p>
                                <p className="text-xs text-muted-foreground">Present</p>
                              </div>
                              <div className="text-center p-3 bg-red-50 rounded-lg">
                                <XCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                                <p className="text-2xl font-bold text-red-600">{course.attendance?.absent}</p>
                                <p className="text-xs text-muted-foreground">Absent</p>
                              </div>
                              <div className="text-center p-3 bg-amber-50 rounded-lg">
                                <Clock className="h-5 w-5 text-amber-600 mx-auto mb-1" />
                                <p className="text-2xl font-bold text-amber-600">{course.attendance?.late}</p>
                                <p className="text-xs text-muted-foreground">Late</p>
                              </div>
                              <div className="text-center p-3 bg-blue-50 rounded-lg">
                                <AlertCircle className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                                <p className="text-2xl font-bold text-blue-600">{course.attendance?.excused}</p>
                                <p className="text-xs text-muted-foreground">Excused</p>
                              </div>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>

                      {/* Assignments */}
                      <AccordionItem value="assignments">
                        <AccordionTrigger>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Assignments & Grades
                            <Badge variant="outline" className="ml-2">
                              {course.assignments?.length || 0} assignments
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {course.assignments?.length === 0 ? (
                            <p className="text-muted-foreground text-sm py-2">No assignments yet</p>
                          ) : (
                            <div className="space-y-3 py-2">
                              {course.assignments.map((assignment, aIdx) => (
                                <div key={aIdx} className="p-3 bg-muted/50 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{assignment.title}</span>
                                      <Badge variant="secondary" className="capitalize text-xs">
                                        {assignment.type}
                                      </Badge>
                                    </div>
                                    {assignment.status === 'graded' ? (
                                      <Badge className={getGradeColor(assignment.percentage || 0)}>
                                        {assignment.points_earned}/{assignment.max_points}
                                        <span className="ml-1">({assignment.percentage}%)</span>
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline">{assignment.status}</Badge>
                                    )}
                                  </div>
                                  {assignment.percentage !== null && (
                                    <Progress value={assignment.percentage} className="h-2 mb-2" />
                                  )}
                                  {assignment.feedback && (
                                    <p className="text-sm text-muted-foreground mt-2 p-2 bg-background rounded">
                                      <span className="font-medium">Feedback:</span> {assignment.feedback}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>

                      {/* Instructor Notes */}
                      <AccordionItem value="notes">
                        <AccordionTrigger>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-4 w-4" />
                            Instructor Notes
                            <Badge variant="outline" className="ml-2">
                              {course.notes?.length || 0} notes
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {course.notes?.length === 0 ? (
                            <p className="text-muted-foreground text-sm py-2">No notes shared yet</p>
                          ) : (
                            <div className="space-y-3 py-2">
                              {course.notes.map((note, nIdx) => (
                                <div key={nIdx} className="p-3 bg-muted/50 rounded-lg">
                                  <div className="flex items-start gap-3">
                                    {getNoteTypeIcon(note.type)}
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between mb-1">
                                        <Badge variant="secondary" className="capitalize text-xs">
                                          {note.type}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">
                                          {new Date(note.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <p className="text-sm">{note.content}</p>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        — {note.instructor_name}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Unable to load progress data</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
